import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
    ApplySecurityCsvImport,
    DryRunSecurityCsvImport,
    ExportSecurityCsvTemplate,
    GetMeetingRoomQuotaDefaults,
    UpdateMeetingRoomQuotaDefaults,
    GetQuotationDefaults,
    UpdateQuotationDefaults,
} from '../services/backend';
import { Notify, NotifyError } from '../utils/functions';

export function useGetMeetingRoomQuotaDefaults() {
    return useQuery(['meeting-room-quota-defaults'], () => GetMeetingRoomQuotaDefaults(), {
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
        select: (data) => data?.data || {},
        onError: (error) => {
            NotifyError('Failed to fetch default meeting room quota settings', error);
            console.error('Error fetching default meeting room quota settings:', error);
        }
    });
}

export function useUpdateMeetingRoomQuotaDefaults() {
    const queryClient = useQueryClient();

    return useMutation((payload) => UpdateMeetingRoomQuotaDefaults(payload), {
        onSuccess: (response) => {
            const updatedUsers = response?.data?.bulkUpdate?.updatedUsers || 0;
            const applied = response?.data?.bulkUpdate?.applied;
            const message = applied
                ? `Default quota updated and applied to ${updatedUsers} users`
                : 'Default quota settings updated successfully';

            Notify(message, 'success');
            queryClient.invalidateQueries('meeting-room-quota-defaults');
            queryClient.invalidateQueries('users');
        },
        onError: (error) => {
            NotifyError('Failed to update default meeting room quota settings', error);
            console.error('Error updating default meeting room quota settings:', error);
        }
    });
}

export function useGetQuotationDefaults() {
    return useQuery(['quotation-defaults'], () => GetQuotationDefaults(), {
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
        select: (data) => data?.data?.defaults || {},
        onError: (error) => {
            NotifyError('Failed to fetch quotation defaults', error);
            console.error('Error fetching quotation defaults:', error);
        }
    });
}

export function useUpdateQuotationDefaults() {
    const queryClient = useQueryClient();

    return useMutation((payload) => UpdateQuotationDefaults(payload), {
        onSuccess: (response) => {
            Notify('Quotation defaults updated successfully', 'success');
            queryClient.invalidateQueries('quotation-defaults');
        },
        onError: (error) => {
            NotifyError('Failed to update quotation defaults', error);
            console.error('Error updating quotation defaults:', error);
        }
    });
}

export function useExportSecurityCsvTemplate() {
    return useMutation(() => ExportSecurityCsvTemplate(), {
        onError: (error) => {
            NotifyError('Failed to export security CSV template', error);
            console.error('Error exporting security CSV template:', error);
        }
    });
}

export function useDryRunSecurityCsvImport() {
    return useMutation((payload) => DryRunSecurityCsvImport(payload), {
        onError: (error) => {
            NotifyError('Failed to validate CSV import', error);
            console.error('Error validating security CSV import:', error);
        }
    });
}

export function useApplySecurityCsvImport() {
    return useMutation((payload) => ApplySecurityCsvImport(payload), {
        onSuccess: (response) => {
            const updatedBookings = response?.data?.summary?.updatedBookings || 0;
            Notify(`Security import applied successfully. Updated ${updatedBookings} bookings.`, 'success');
        },
        onError: (error) => {
            NotifyError('Failed to apply CSV import', error);
            console.error('Error applying security CSV import:', error);
        }
    });
}
